
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, Download, AlertTriangle, FileSpreadsheet, CheckCircle2, FileX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { useScheduleUploader } from "../hooks/useScheduleUploader";
import { useLanguage } from "@/contexts/LanguageContext";

interface BulkUploadShiftsProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  onSuccess: () => void;
}

const BulkUploadShifts: React.FC<BulkUploadShiftsProps> = ({
  isOpen,
  onClose,
  teamId,
  onSuccess
}) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  
  const {
    uploadFile,
    downloadTemplate,
    isUploading,
    progress,
    results,
    errors
  } = useScheduleUploader(teamId);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileError(null);
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      
      const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();
      if (fileExtension !== 'xlsx' && fileExtension !== 'xls') {
        setFileError(t("onlyExcelFilesAllowed") || "Only Excel files (.xlsx, .xls) are allowed");
        setFile(null);
        return;
      }
      
      if (selectedFile.size > 5 * 1024 * 1024) {
        setFileError(t("fileTooLarge") || "File is too large. Maximum size is 5MB.");
        setFile(null);
        return;
      }
      
      setFile(selectedFile);
      console.log("File selected:", selectedFile.name);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast({
        variant: "destructive",
        title: t("error"),
        description: t("pleaseSelectFile") || "Please select a file first",
      });
      return;
    }

    try {
      console.log("Starting file upload:", file.name);
      const uploadedShifts = await uploadFile(file);
      console.log("Upload completed:", uploadedShifts);
      
      if (uploadedShifts && uploadedShifts.length > 0) {
        toast({
          title: t("success"),
          description: t("shiftsUploaded", {count: uploadedShifts.length}) || `${uploadedShifts.length} shifts have been uploaded successfully`,
        });
        onSuccess();
      } else {
        toast({
          variant: "destructive",
          title: t("warning"),
          description: t("noShiftsUploaded") || "No shifts were uploaded. Please check the file format and try again.",
        });
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      
      let errorMessage = String(error);
      
      if (errorMessage.includes("No valid shifts found")) {
        errorMessage = t("noValidShiftsFound") || "No valid shifts were found in the file. Please check the file format and try again.";
      } else if (errorMessage.includes("Missing required columns")) {
        errorMessage = t("missingRequiredColumns") || "Missing required columns in the file. Please use the template.";
      } else if (errorMessage.includes("Failed to parse")) {
        errorMessage = t("failedToParseFile") || "Failed to parse the file. Please make sure it's a valid Excel file.";
      } else if (errorMessage.includes("Invalid date format")) {
        errorMessage = t("invalidDateFormat") || "Invalid date format. Please make sure dates are in DD/MM/YYYY, MM/DD/YYYY format, or D/M/YY for short formats.";
      }
      
      toast({
        variant: "destructive",
        title: t("error"),
        description: errorMessage || t("errorUploadingShifts") || "Error uploading shifts",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("bulkUploadShifts") || "Bulk Upload Shifts"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Alert variant="default" className="bg-blue-50 border-blue-200">
            <FileSpreadsheet className="h-4 w-4 text-blue-600" />
            <AlertTitle className="text-blue-700">{t("importantNotes") || "Important Notes"}</AlertTitle>
            <AlertDescription className="text-blue-700">
              <p className="mb-1 font-medium">{t("downloadTemplateFirstAndFollow") || "Download the template first and follow these steps:"}</p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>{t("useExactColumnsFromTemplate") || "Use the exact columns from the template"}</li>
                <li>
                  {t("dateFormatSupported") || "Date formats supported:"} 
                  <ul className="list-disc pl-5 text-xs mt-1">
                    <li>DD/MM/YYYY (e.g., 01/02/2025)</li>
                    <li>D/M/YY (e.g., 10/2/25)</li>
                    <li>MM/DD/YYYY (e.g., 02/01/2025)</li>
                    <li>DD-MM-YYYY, DD.MM.YYYY</li>
                  </ul>
                </li>
                <li>
                  {t("timeFormatsSupported") || "Time formats supported:"} 
                  <ul className="list-disc pl-5 text-xs mt-1">
                    <li>HH:MM (e.g., 08:00)</li>
                    <li>H:MM (e.g., 8:00)</li>
                    <li>HH.MM (e.g., 08.00)</li>
                    <li>HHMM (e.g., 0800)</li>
                    <li>12-hour format (e.g., 8AM, 2PM)</li>
                  </ul>
                </li>
                <li>{t("guardNamesExactMatch") || "Guard names must exactly match how they appear in the system"}</li>
                <li>{t("doNotChangeHeaders") || "Do not change or remove column headers"}</li>
                <li>{t("saveAsExcel") || "Save as an Excel (.xlsx) file"}</li>
                <li>
                  {"For ΡΕΠΟ (day off), you can use 00:00 to 00:00 to indicate the whole day"}
                </li>
              </ol>
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label htmlFor="file-upload">{t("uploadExcelFile") || "Upload Excel File"}</Label>
            <div className="flex gap-2">
              <Input
                id="file-upload"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                disabled={isUploading}
                className="flex-1"
              />
              <Button 
                variant="outline"
                onClick={downloadTemplate}
                className="flex items-center gap-2"
                disabled={isUploading}
              >
                <Download className="h-4 w-4" />
                {t("template") || "Template"}
              </Button>
            </div>
            {fileError && (
              <p className="text-sm text-destructive mt-1">{fileError}</p>
            )}
            <p className="text-sm text-muted-foreground mt-1">
              {t("downloadTemplateFirst") || "Download the template first to ensure correct format"}
            </p>
          </div>

          {isUploading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t("uploading") || "Uploading"}...</span>
                <span className="text-sm font-medium">{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          )}

          {results && results.length > 0 && (
            <Alert variant="default" className="bg-green-50 border-green-200">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertTitle className="text-green-700">{t("uploadSuccess") || "Upload Successful"}</AlertTitle>
              <AlertDescription className="text-green-600">
                {t("shiftsAdded", {count: results.length}) || `${results.length} shifts have been successfully added`}
              </AlertDescription>
            </Alert>
          )}

          {errors && errors.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>{t("uploadErrors") || "Upload Errors"}</AlertTitle>
              <AlertDescription>
                <ul className="list-disc pl-5 text-sm space-y-1 mt-2">
                  {errors.slice(0, 5).map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                  {errors.length > 5 && (
                    <li className="italic">
                      {t("andMoreErrors", {count: errors.length - 5}) || `And ${errors.length - 5} more errors...`}
                    </li>
                  )}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {!file && !isUploading && errors.length === 0 && (
            <div className="flex justify-center p-6 bg-gray-50 rounded-md border border-dashed border-gray-300">
              <div className="text-center">
                <FileX className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  {t("noFileSelected") || "No file selected"}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {t("downloadTemplateAndFill") || "Download the template, fill it, and upload it here"}
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={onClose} disabled={isUploading}>
              {t("cancel") || "Cancel"}
            </Button>
            <Button 
              onClick={handleUpload} 
              disabled={!file || isUploading}
              className="flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              {isUploading ? (t("uploading") || "Uploading...") : (t("upload") || "Upload")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkUploadShifts;
